import { useLocation } from "react-router-dom";
import { navigation } from "./navigation-items";
import LogoutButton from "../authentication/LogoutButton";
import { useTenantNavigation } from "@/hooks/navigation/useTenantNavigation";
import { useOrganization } from "@/services/organization/getOrganizationDetails";

// Icons
import { X, AlertTriangle } from "lucide-react";

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSidebar({ isOpen, onClose }: MobileNavProps) {
  // Store the current organization
  const { organization, loading, error } = useOrganization();
  const location = useLocation();
  const { getTenantPath } = useTenantNavigation();

  const isPathActive = (path: string) => {
    const currentPath = location.pathname;
    const tenantPath = getTenantPath(path);
    
    // Special case for dashboard - consider all event-related paths as part of dashboard
    if (path === '/dashboard') {
      return currentPath === tenantPath || 
             currentPath.includes('/events/') || 
             currentPath.includes('/event/');
    }
    
    return currentPath === tenantPath;
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Dark overlay */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Navigation menu */}
      <div className="fixed inset-y-0 left-0 w-3/4 max-w-sm bg-background shadow-xl animate-in slide-in-from-left">
        <div className="flex h-full flex-col px-4 py-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-semibold truncate whitespace-nowrap overflow-hidden flex-1" title={organization?.name}>
                {loading ? "Loading..." : error ? "Error" : organization?.name || "Organization"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-accent"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 space-y-2">
            {navigation.map((item) => {
              const isActive = isPathActive(item.href);
              return (
                <a
                  key={item.name}
                  href={getTenantPath(item.href)}
                  onClick={onClose}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </a>
              );
            })}
          </nav>

          {/* Support and help section */}
          <div className="mt-auto pt-6 border-t border-gray-200 space-y-3">
            <a
              href="mailto:support@tricode.cloud?subject=%5BBug%20Report%5D%20Issue%20with%20TriEvents%20Platform"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              onClick={onClose}
            >
              <AlertTriangle className="h-5 w-5" />
              <span>Report an Issue</span>
            </a>

            <LogoutButton />
          </div>
        </div>
      </div>
    </div>
  );
}
